import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { MemberInfo } from "../../../types/member";

import { getQualtricsList } from "../../../libs/qualtrics";

import { withdrawMember } from "../../../services/memberServices";
import { logout } from "../../../services/authServices";

import { useUserContext } from "../../../contexts/userContext";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";
import useShowQualtrics from "../../../hooks/useShowQualtrics";

import CommonButton from "../../Button";
import CommonPopup from "..";

type Props = { popup: string; onClose?: () => void };

export default function WithDrawPopup({ popup, onClose }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useUserContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [showQualtrics] = useShowQualtrics({ open });

  const onSuccess = () => {
    if (!popup) return;
    if (!location.pathname) return;

    const qualtricsList =
      getQualtricsList()[`${location.pathname}:${popup}:onSuccess`];
    if (!qualtricsList || qualtricsList.length === 0) return;

    const { isLoading, floor, building, ...memberInfo } = user;
    showQualtrics(qualtricsList.join(","), memberInfo as MemberInfo);
  };

  return (
    <CommonPopup
      isOpen={popup === "withdraw"}
      title="샌디 회원탈퇴"
      onClose={onClose}
      padding={0}
    >
      {loadingComponent}
      <div className="flex flex-col items-center">
        <div className="p-5 pt-0">
          <div className="flex flex-col gap-2.5 py-2.5 items-center text-center whitespace-pre-line text-brand-neutral-60 text-sm">
            <MyInfoConfigWithdrawIcon />
            {
              "샌디 회원을 탈퇴하시겠습니까?\n같은 이메일로는 다시 가입할 수 없어요."
            }
          </div>
        </div>
        <div className="flex w-full">
          <CommonButton
            variant="secondary"
            className="rounded-none h-bottom-button w-[170px]"
            onClick={onClose}
          >
            다시 생각해볼게요
          </CommonButton>
          <CommonButton
            className="rounded-none h-bottom-button flex-1"
            onClick={async () => {
              onSuccess();

              setIsLoading(true);
              try {
                const res = await withdrawMember();
                if (res.status === HttpStatusCode.Ok) {
                  open("회원 탈퇴가 완료되었습니다.");
                  logout();
                  navigate("/auth/login");
                }
              } catch (e: any) {
                if (e?.response?.data?.error) {
                  open(e.response.data.error?.replace(". ", ".\n"));
                } else {
                  open("회원 탈퇴에 실패했습니다.");
                }
              } finally {
                setIsLoading(false);
              }
            }}
          >
            회원 탈퇴
          </CommonButton>
        </div>
      </div>
      {simplePopupComponent}
    </CommonPopup>
  );
}

export const MyInfoConfigWithdrawIcon = () => (
  <svg
    width="62"
    height="60"
    viewBox="0 0 62 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="60" height="60" fill="white" />
    <path
      d="M5 30C5 43.8071 16.1929 55 30 55C43.8071 55 55 43.8071 55 30C55 16.1929 43.8071 5 30 5C16.1929 5 5 16.1929 5 30Z"
      stroke="#1D1929"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <rect x="37" y="21" width="3" height="8" fill="black" />
    <rect x="20" y="21" width="3" height="8" fill="black" />
    <path
      d="M21 40C22 39.1667 25.2 37.5 30 37.5C34.8 37.5 38 39.1667 39 40"
      stroke="black"
      strokeWidth="3"
      strokeLinecap="square"
    />
    <path
      d="M46 39.5926C46 36.037 50.9259 29.4691 53 27C55.3333 29.2222 60 35.8889 60 39.5926C60 43.2963 56.8889 47 53 47C49.1111 47 46 44.037 46 39.5926Z"
      fill="black"
    />
    <path
      d="M53 27L54.0345 25.9138C53.7346 25.6282 53.3301 25.4793 52.9167 25.5023C52.5033 25.5253 52.1178 25.7182 51.8514 26.0352L53 27ZM51.8514 26.0352C50.7814 27.3091 48.9861 29.6262 47.4488 32.0963C46.6801 33.3312 45.9578 34.6332 45.4232 35.8818C44.9004 37.1028 44.5 38.4017 44.5 39.5926H47.5C47.5 39.0057 47.7153 38.1503 48.181 37.0626C48.6348 36.0026 49.2736 34.8417 49.9957 33.6815C51.4398 31.3614 53.1445 29.1601 54.1486 27.9648L51.8514 26.0352ZM44.5 39.5926C44.5 44.8294 48.2471 48.5 53 48.5V45.5C49.9751 45.5 47.5 43.2446 47.5 39.5926H44.5ZM53 48.5C57.8473 48.5 61.5 43.9885 61.5 39.5926H58.5C58.5 42.6041 55.9305 45.5 53 45.5V48.5ZM61.5 39.5926C61.5 38.3886 61.1301 37.0708 60.6264 35.8161C60.1135 34.5386 59.4155 33.2141 58.658 31.9636C57.1552 29.4828 55.3262 27.144 54.0345 25.9138L51.9655 28.0862C53.0071 29.0782 54.6781 31.1839 56.092 33.5179C56.7929 34.6748 57.4073 35.8502 57.8424 36.9339C58.2866 38.0403 58.5 38.9448 58.5 39.5926H61.5Z"
      fill="white"
    />
  </svg>
);
